//----------------------------------------------------------------------------------------------------------------------
// Scene Engine
//
// Wraps BabylonJS scene lifecycle, mesh loading, physics, cameras, and lights.
// All built-in loaders (glTF, OBJ, STL, etc.) are registered via registerBuiltInLoaders()
// and loaded on-demand when first used.
//----------------------------------------------------------------------------------------------------------------------

import {
    AbstractEngine,
    AbstractMesh,
    AssetContainer,
    DirectionalLight,
    FreeCamera,
    HavokPlugin,
    HemisphericLight,
    ImportMeshAsync,
    Light,
    LoadAssetContainerAsync,
    Mesh,
    MeshBuilder,
    PhysicsAggregate,
    PhysicsShapeType,
    PointLight,
    RectAreaLight,
    Scene,
    SpotLight,
    Vector3,
} from '@babylonjs/core';

// Register all built-in loaders (glTF, OBJ, STL, etc.) -- loaded on-demand when first used
import { registerBuiltInLoaders } from '@babylonjs/loaders/dynamic';
registerBuiltInLoaders();
import type { HavokPhysicsWithBindings } from '@babylonjs/havok';

// Interfaces
import type { GameCanvas } from '../interfaces/game.ts';
import type { Disposable } from '../interfaces/lifecycle.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';

// Utils
import { type LoggingUtility } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

export class SceneEngine implements Disposable
{
    private _canvas : GameCanvas;
    private _engine : AbstractEngine;
    private _havok : HavokPhysicsWithBindings;
    private _log : LoggerInterface;

    constructor(canvas : GameCanvas, engine : AbstractEngine, havok : HavokPhysicsWithBindings, logger : LoggingUtility)
    {
        this._canvas = canvas;
        this._engine = engine;
        this._havok = havok;
        this._log = logger.getLogger('SceneEngine');
    }

    /**
     * Creates a new scene with physics enabled
     */
    public createScene() : Scene
    {
        return new Scene(this._engine);
    }

    public enablePhysics(
        scene : Scene,
        gravityVector : Vector3 = new Vector3(0, -9.8, 0),
        floatingOriginWorldRadius ?: number
    ) : void
    {
        this._log.debug(
            `Enabling physics with gravity (${ gravityVector.x }, ${ gravityVector.y }, ${ gravityVector.z })...`
        );

        const pluginOptions = floatingOriginWorldRadius !== undefined
            ? { floatingOriginWorldRadius }
            : undefined;

        // Each scene gets its own HavokPlugin so that scene.dispose() doesn't corrupt the shared WASM module.
        const plugin = new HavokPlugin(true, this._havok, pluginOptions);
        scene.enablePhysics(gravityVector, plugin);
    }

    // Camera Utilities
    /**
     * Creates a free camera at the specified position, targeting the origin. Attaches controls to the provided
     * canvas, or falls back to the engine's default canvas.
     * @param name
     * @param position
     * @param scene
     * @param canvas - Overrides the default canvas for input controls
     */
    public createFreeCamera(
        name : string,
        position : Vector3,
        scene : Scene,
        canvas ?: GameCanvas
    ) : FreeCamera
    {
        const camera = new FreeCamera(name, position, scene);
        camera.setTarget(Vector3.Zero());

        canvas = canvas ?? this._canvas;

        if(canvas)
        {
            camera.attachControl(canvas, true);
        }

        return camera;
    }

    // Lighting Utilities
    /**
     * Creates a hemispheric light (ambient lighting)
     * @param name
     * @param direction
     * @param scene
     * @param intensity - Default: 0.7
     */
    public createHemisphericLight(
        name : string,
        direction : Vector3,
        scene : Scene,
        intensity = 0.7
    ) : HemisphericLight
    {
        const light = new HemisphericLight(name, direction, scene);
        light.intensity = intensity;
        return light;
    }

    /**
     * Creates a directional light (sun-like lighting)
     * @param name
     * @param direction
     * @param scene
     * @param intensity - Default: 1.0
     */
    public createDirectionalLight(
        name : string,
        direction : Vector3,
        scene : Scene,
        intensity = 1.0
    ) : DirectionalLight
    {
        const light = new DirectionalLight(name, direction, scene);
        light.intensity = intensity;
        return light;
    }

    /**
     * Creates a point light (bulb-like lighting)
     * @param name
     * @param position
     * @param scene
     * @param intensity - Default: 1.0
     */
    public createPointLight(
        name : string,
        position : Vector3,
        scene : Scene,
        intensity = 1.0
    ) : PointLight
    {
        const light = new PointLight(name, position, scene);
        light.intensity = intensity;
        return light;
    }

    /**
     * Creates a spotlight (flashlight-like lighting)
     * @param name
     * @param position
     * @param direction
     * @param angle - Cone angle in radians
     * @param exponent - Controls light falloff from center to edge of cone
     * @param scene
     * @param intensity - Default: 1.0
     */
    public createSpotLight(
        name : string,
        position : Vector3,
        direction : Vector3,
        angle : number,
        exponent : number,
        scene : Scene,
        intensity = 1.0
    ) : SpotLight
    {
        const light = new SpotLight(name, position, direction, angle, exponent, scene);
        light.intensity = intensity;
        return light;
    }

    /**
     * Creates a rectangular area light (emits from a flat rectangle in the -Z direction)
     * @param name
     * @param position
     * @param width
     * @param height
     * @param scene
     * @param intensity - Default: 1.0
     */
    public createRectAreaLight(
        name : string,
        position : Vector3,
        width : number,
        height : number,
        scene : Scene,
        intensity = 1.0
    ) : RectAreaLight
    {
        const light = new RectAreaLight(name, position, width, height, scene);
        light.intensity = intensity;
        return light;
    }

    // Mesh Creation Utilities
    /**
     * Creates a sphere mesh. Defaults: diameter 1, 32 segments.
     * @param name
     * @param options
     * @param scene
     */
    public createSphere(
        name : string,
        options : { diameter ?: number; segments ?: number } = {},
        scene : Scene
    ) : Mesh
    {
        const defaultOptions = { diameter: 1, segments: 32 };
        return MeshBuilder.CreateSphere(name, { ...defaultOptions, ...options }, scene);
    }

    /**
     * Creates a box mesh. Default size: 1.
     * @param name
     * @param options
     * @param scene
     */
    public createBox(
        name : string,
        options : { size ?: number; width ?: number; height ?: number; depth ?: number } = {},
        scene : Scene
    ) : Mesh
    {
        const defaultOptions = { size: 1 };
        return MeshBuilder.CreateBox(name, { ...defaultOptions, ...options }, scene);
    }

    /**
     * Creates a ground mesh. Defaults: 6x6 with 2 subdivisions.
     * @param name
     * @param options
     * @param scene
     */
    public createGround(
        name : string,
        options : { width ?: number; height ?: number; subdivisions ?: number } = {},
        scene : Scene
    ) : Mesh
    {
        const defaultOptions = { width: 6, height: 6, subdivisions: 2 };
        return MeshBuilder.CreateGround(name, { ...defaultOptions, ...options }, scene);
    }

    /**
     * Creates a cylinder mesh. Defaults: height 2, diameter 1 (top and bottom).
     * @param name
     * @param options
     * @param scene
     */
    public createCylinder(
        name : string,
        options : { height ?: number; diameterTop ?: number; diameterBottom ?: number } = {},
        scene : Scene
    ) : Mesh
    {
        const defaultOptions = { height: 2, diameterTop: 1, diameterBottom: 1 };
        return MeshBuilder.CreateCylinder(name, { ...defaultOptions, ...options }, scene);
    }

    // Physics Utilities
    /**
     * Adds physics to a mesh. Defaults: mass 1, restitution 0.75, friction 0.5.
     * @param mesh
     * @param shapeType
     * @param physicsProps
     * @param scene
     */
    public addPhysics(
        mesh : Mesh,
        shapeType : PhysicsShapeType,
        physicsProps : { mass ?: number; restitution ?: number; friction ?: number } = {},
        scene : Scene
    ) : PhysicsAggregate
    {
        const defaultProps = { mass: 1, restitution: 0.75, friction: 0.5 };
        return new PhysicsAggregate(mesh, shapeType, { ...defaultProps, ...physicsProps }, scene);
    }

    // Model Loading Utilities
    /**
     * Loads a 3D model/asset into an AssetContainer without adding it to the scene automatically.
     * @param sceneFilename
     * @param scene
     */
    public async loadModel(
        sceneFilename : string,
        scene : Scene
    ) : Promise<AssetContainer>
    {
        this._log.debug(`Loading model: ${ sceneFilename }`);

        try
        {
            const result = await LoadAssetContainerAsync(`${ sceneFilename }`, scene);
            this._log.debug(`Model loaded successfully: ${ sceneFilename }`);
            return result;
        }
        catch (error)
        {
            this._log.error(`Failed to load model: ${ sceneFilename }`, error);
            throw error;
        }
    }

    /**
     * Imports meshes from a file directly into the scene.
     * @param meshNames - Specific meshes to import; empty array imports all
     * @param sceneFilename
     * @param scene
     */
    public async importMeshes(
        meshNames : string[],
        sceneFilename : string,
        scene : Scene
    ) : Promise<{
        meshes : AbstractMesh[];
        particleSystems : unknown[];
        skeletons : unknown[];
        animationGroups : unknown[];
        transformNodes : unknown[];
        geometries : unknown[];
        lights : Light[];
    }>
    {
        this._log.debug(`Importing meshes from: ${ sceneFilename }`);

        try
        {
            // Note: meshNames as empty array means "import nothing", use undefined for "import all"
            const options = meshNames.length > 0 ? { meshNames } : undefined;
            const result = await ImportMeshAsync(`${ sceneFilename }`, scene, options);
            this._log.debug(`Meshes imported successfully: ${ meshNames.length > 0 ? meshNames.join(', ') : 'all' }`);
            return result;
        }
        catch (error)
        {
            this._log.error(`Failed to import meshes from: ${ sceneFilename }`, error);
            throw error;
        }
    }

    /**
     * Tears down the scene engine and cleans up any resources
     */
    async $teardown() : Promise<void>
    {
        this._log.info('Tearing down SceneEngine');

        // Currently no specific resources to clean up
        // The scenes themselves are managed by the LevelManager
        // and physics engine is disposed at the application level

        this._log.info('SceneEngine torn down successfully');
    }
}

//----------------------------------------------------------------------------------------------------------------------
